import { useCart } from "./CartContext";
import { X, Minus, Plus, Trash2 } from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { useNavigate } from "react-router";
import { useLanguage } from "./LanguageContext";
import { useTheme } from "./ThemeInjector";

export function CartDrawer() {
  const { items, isCartOpen, setIsCartOpen, removeItem, updateQuantity, totalPrice, totalItems } =
    useCart();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const theme = useTheme();

  if (!isCartOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/20 backdrop-blur-sm"
        onClick={() => setIsCartOpen(false)}
      />

      {/* Drawer */}
      <div
        className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-[#F7F5F1] shadow-2xl flex flex-col"
        style={{ animation: "slideIn 320ms ease-out" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E6E0D7]">
          <h2
            className="tracking-[0.15em] uppercase"
            style={{ fontFamily: theme.fontHeading, fontSize: "20px", fontWeight: 500, color: theme.fg }}
          >
            {t("cart.title")} ({totalItems})
          </h2>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-1 text-[#7B5E57] hover:text-[#3D3530] transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <p
                className="tracking-[0.1em] mb-2"
                style={{ fontFamily: theme.fontHeading, fontSize: "22px", fontWeight: 400, color: theme.muted }}
              >
                {t("cart.empty")}
              </p>
              <p
                style={{ fontFamily: theme.fontBody, fontSize: "14px", fontWeight: 300, color: theme.muted }}
              >
                {t("cart.empty_desc")}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {items.map((item) => (
                <div key={item.id} className="flex gap-4">
                  <div className="w-20 h-20 rounded-md overflow-hidden shrink-0 bg-[#E9E4DC]">
                    <ImageWithFallback
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-[#3D3530] truncate"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "16px", fontWeight: 500 }}
                    >
                      {item.name}
                    </h3>
                    <p
                      className="text-[#C7A76C] mb-2"
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px", fontWeight: 400 }}
                    >
                      €{item.price}
                    </p>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-7 h-7 rounded-full border border-[#E6E0D7] flex items-center justify-center text-[#6F675F] hover:border-[#C7A76C] transition-colors"
                      >
                        <Minus size={12} />
                      </button>
                      <span
                        className="text-[#3D3530] w-6 text-center"
                        style={{ fontFamily: "'Jost', sans-serif", fontSize: "14px" }}
                      >
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-7 h-7 rounded-full border border-[#E6E0D7] flex items-center justify-center text-[#6F675F] hover:border-[#C7A76C] transition-colors"
                      >
                        <Plus size={12} />
                      </button>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="ml-auto p-1 text-[#9E9E9E] hover:text-[#d4183d] transition-colors"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-[#E6E0D7] space-y-4">
            <div className="flex items-center justify-between">
              <span
                className="tracking-[0.1em] uppercase"
                style={{ fontFamily: theme.fontBody, fontSize: "12px", color: theme.muted }}
              >
                {t("cart.subtotal")}
              </span>
              <span
                className="text-[#3D3530]"
                style={{ fontFamily: "'Jost', sans-serif", fontSize: "18px", fontWeight: 400 }}
              >
                €{totalPrice}
              </span>
            </div>
            <button
              onClick={() => {
                setIsCartOpen(false);
                navigate("/checkout");
              }}
              className="w-full py-4 tracking-[0.25em] uppercase rounded-lg transition-all duration-300"
              style={{ fontFamily: theme.fontBody, fontSize: "12px", fontWeight: 400, backgroundColor: theme.btnBg, color: theme.btnText, borderRadius: theme.btnRadius }}
            >
              {t("cart.proceed")}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </>
  );
}